/**
 * Guided-tour telemetry catalog.
 *
 * Rides the existing spine — `track()` batches to `POST /api/events`, which
 * persists to `fg_events` and fans a privacy-guarded subset to PostHog — so
 * there is no new table and no new pipeline. `activity.ts` already stamps
 * `is_demo`, `app_route_mode`, `site_slug`, `workbook_id` and `cfo_page` onto
 * every event, which is what makes demo-versus-customer segmentation free.
 *
 * Two rules:
 *  - **No PII.** `leadIdentify` carries the `demo_leads` row id, never the
 *    email. The email is already first-party in `demo_leads` and joinable.
 *  - **`anchorMissed` is health, not funnel.** It is the production canary for
 *    selector drift: CI catches breakage on one viewport, this catches it on
 *    every real viewport, data shape and connection speed.
 *
 * Note that `activity.ts`'s `routeContext()` is spread only onto its own `app.*`
 * events — a plain `track()` call does **not** inherit `is_demo`. So the two
 * fields the funnel actually slices on are stamped here, or the founder
 * dashboard's demo-versus-customer split silently reads zero.
 */

import { track } from '../../analytics'
import { isDemoSession } from '../../data/demoSession'
import { parseCfoRouteState } from '../parity-dev/cfo/cfoRoutes'

export const TOUR_SURFACE = 'cfo_tour'

export const TOUR_EVENTS = {
  open: 'tour.open',
  stepView: 'tour.step_view',
  stepComplete: 'tour.step_complete',
  stepBack: 'tour.step_back',
  chapterView: 'tour.chapter_view',
  ctaPrimary: 'tour.cta_primary',
  ctaSecondary: 'tour.cta_secondary',
  ctaConvert: 'tour.cta_convert',
  leadIdentify: 'tour.lead_identify',
  checklistItem: 'tour.checklist_item',
  theirNumbers: 'tour.their_numbers',
  finished: 'tour.finished',
  exit: 'tour.exit',
  shareLinkOpen: 'tour.share_link_open',
  /** Selector drift alarm — an anchor never resolved and the step degraded. */
  anchorMissed: 'tour.anchor_missed',
} as const

export type TourTelemetryEvent = (typeof TOUR_EVENTS)[keyof typeof TOUR_EVENTS]

/** Why a tour ended without finishing — the drop-off story in one field. */
export type TourExitReason = 'close_button' | 'escape' | 'navigated_away' | 'error' | 'declined_start'

/** Stamped on every tour event so a funnel can be sliced without a join. */
export type TourEventContext = {
  tour_slug: string
  tour_version: number
  /** `db` or `bundled` — makes a stale fallback visible instead of silent. */
  tour_source: string
  context: string
  embed: boolean
  step_count: number
  industry?: string | null
  share_token_id?: string | null
  /** A/B arm; the whole experiment framework is this one prop plus SQL. */
  variant?: string | null
}

export type TourStepContext = {
  step_id: string
  step_index: number
  chapter_id: string
}

export function trackTour(
  event: TourTelemetryEvent,
  ctx: TourEventContext,
  props: Record<string, unknown> = {},
): void {
  const route = typeof window === 'undefined' ? null : parseCfoRouteState(window.location)
  track({
    event,
    surface: TOUR_SURFACE,
    workbook_id: route?.workbookId || null,
    props: {
      ...ctx,
      // Not inherited from routeContext() — see the note above.
      is_demo: typeof window === 'undefined' ? false : isDemoSession(),
      cfo_page: route?.page || null,
      site_slug: route?.siteSlug || null,
      ...props,
    },
  })
}
